import React, { useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { ReactComponent as Wordmark } from "../assets/brand/clash-wordmark.svg";
import "./Button.scss";
import "./Display.scss";

/** The Clash wordmark as inline SVG; it paints in the CSS `color` where it sits. Set a height. */
export function BrandLogo({ className, title = "The Clash" }: { className?: string; title?: string }) {
  const classes = ["ui-brand-logo", className].filter(Boolean).join(" ");
  return title ? (
    <Wordmark className={classes} role="img" aria-label={title} focusable="false" />
  ) : (
    <Wordmark className={classes} aria-hidden="true" focusable="false" />
  );
}

export type StatCardProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
  /** A muted line under the value (a trend, a breakdown…) */
  hint?: React.ReactNode;
  /** The accent of the icon and the corner stroke */
  tone?: "spark" | "brand" | "ok" | "danger" | "neutral";
  onClick?: () => void;
  className?: string;
};

/** A KPI tile: label, big display-font number, icon. */
export function StatCard({ label, value, icon, hint, tone = "spark", onClick, className }: StatCardProps) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={["ui-stat", `ui-stat--${tone}`, onClick ? "is-clickable" : "", className].filter(Boolean).join(" ")}
    >
      <div className="ui-stat__top">
        <span className="ui-stat__label">{label}</span>
        {icon ? <span className="ui-stat__icon">{icon}</span> : null}
      </div>
      <span className="ui-stat__value">{value}</span>
      {hint ? <span className="ui-stat__hint">{hint}</span> : null}
    </Tag>
  );
}

/** A row above a list: filters at the left, actions at the right; wraps on narrow screens. */
export function Toolbar({ children, actions, className }: { children?: React.ReactNode; actions?: React.ReactNode; className?: string }) {
  return (
    <div className={["ui-toolbar", className].filter(Boolean).join(" ")}>
      <div className="ui-toolbar__main">{children}</div>
      {actions ? <div className="ui-toolbar__actions">{actions}</div> : null}
    </div>
  );
}

const initialsOf = (name?: string) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";

/** A round photo, or the person's initials on the orange tint. */
export function Avatar({ name, src, size = 36, className }: { name?: string; src?: string | null; size?: number; className?: string }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.38)) };
  return src && !broken ? (
    <img className={["ui-avatar", className].filter(Boolean).join(" ")} style={style} src={src} alt={name ?? ""} onError={() => setBroken(true)} />
  ) : (
    <span className={["ui-avatar", "ui-avatar--initials", className].filter(Boolean).join(" ")} style={style} aria-hidden={name ? undefined : true}>
      {initialsOf(name)}
    </span>
  );
}

export type DescriptionItem = { label: React.ReactNode; value: React.ReactNode; /** span the whole row */ wide?: boolean };

/** Label / value pairs in a grid: the details of a player, an order, a booking. */
export function DescriptionList({
  items,
  columns = 2,
  mobileColumns = 1,
  className,
}: {
  items: DescriptionItem[];
  columns?: 1 | 2 | 3;
  /** Columns on phones (default 1); 2 suits short values like numbers and Yes/No */
  mobileColumns?: 1 | 2;
  className?: string;
}) {
  return (
    <dl className={["ui-dl", `ui-dl--${columns}`, mobileColumns === 2 ? "ui-dl--m2" : "", className].filter(Boolean).join(" ")}>
      {items.map((item, i) => (
        <div key={i} className={`ui-dl__item${item.wide ? " ui-dl__item--wide" : ""}`}>
          <dt>{item.label}</dt>
          <dd>{item.value === null || item.value === undefined || item.value === "" ? "—" : item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export type UploadBoxProps = {
  /** The current image, if any */
  previewUrl?: string | null;
  /** A file was picked or dropped */
  onPick: (file: File) => void;
  /** Remove the current image */
  onRemove?: () => void;
  uploading?: boolean;
  accept?: string;
  /** "PNG or JPG, up to 5 MB" */
  hint?: React.ReactNode;
  label?: React.ReactNode;
  disabled?: boolean;
  /** Preview box shape */
  aspect?: "wide" | "square";
  /** The preview's buttons, when a page words them differently */
  replaceLabel?: React.ReactNode;
  removeLabel?: React.ReactNode;
  className?: string;
};

/** Click or drop an image: a dashed box with a preview, uploading state and remove. Only presentation: the caller uploads. */
export function UploadBox({
  previewUrl,
  onPick,
  onRemove,
  uploading = false,
  accept = "image/*",
  hint,
  label = "Click or drop an image",
  disabled = false,
  aspect = "wide",
  replaceLabel = "Replace",
  removeLabel = "Remove",
  className,
}: UploadBoxProps) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const pick = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onPick(file);
    if (input.current) input.current.value = "";
  };
  return (
    <div
      className={["ui-upload", `ui-upload--${aspect}`, over ? "is-over" : "", disabled || uploading ? "is-disabled" : "", className].filter(Boolean).join(" ")}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled && !uploading) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled && !uploading) pick(e.dataTransfer.files);
      }}
    >
      {previewUrl ? (
        <div className="ui-upload__preview">
          <img src={previewUrl} alt="" />
          <div className="ui-upload__preview-actions">
            <button type="button" className="ui-upload__chip" onClick={() => input.current?.click()} disabled={disabled || uploading}>
              <ImagePlus size={14} /> {replaceLabel}
            </button>
            {onRemove ? (
              <button type="button" className="ui-upload__chip ui-upload__chip--danger" onClick={onRemove} disabled={disabled || uploading}>
                <Trash2 size={14} /> {removeLabel}
              </button>
            ) : null}
          </div>
          {uploading ? <div className="ui-upload__busy"><span className="ui-btn__spinner" /></div> : null}
        </div>
      ) : (
        <button type="button" className="ui-upload__empty" onClick={() => input.current?.click()} disabled={disabled || uploading}>
          {uploading ? <span className="ui-btn__spinner" aria-hidden="true" /> : <ImagePlus size={22} aria-hidden="true" />}
          <span className="ui-upload__label">{uploading ? "Uploading…" : label}</span>
          {hint ? <span className="ui-upload__hint">{hint}</span> : null}
        </button>
      )}
      <input ref={input} type="file" accept={accept} hidden onChange={(e) => pick(e.target.files)} />
    </div>
  );
}
